import React from "react";
import { Mail, Phone, MessageCircle, MapPin, Send, Instagram } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import {
  contactInfo,
  companyDetails,
  officeAddresses,
  media,
} from "@/i18n/translations";
import SEO from "@/components/SEO";
import { FadeUp, PageHeader } from "@/components/ui-primitives";
import ContactForm from "@/components/ContactForm";

const OfficeCard = ({ office, testId }) => (
  <FadeUp>
    <div className="glass-light p-6 h-full" data-testid={testId}>
      <MapPin size={18} strokeWidth={1.5} className="text-brand mb-4" />
      <div className="overline mb-3">{office.label}</div>
      <address className="not-italic text-navy/80 text-sm leading-relaxed">
        {office.lines.map((l, i) => (
          <div key={i}>{l}</div>
        ))}
      </address>
    </div>
  </FadeUp>
);

const Contact = () => {
  const { t, lang } = useLanguage();
  const offices = officeAddresses[lang];

  return (
    <>
      <SEO
        path="/contact"
        title={lang === "ru" ? "Контакты" : "Contact"}
        description={
          lang === "ru"
            ? "Свяжитесь с Rota Yachting — Email, WhatsApp, телефон и офисы в Синопе, Стамбуле и Амасре."
            : "Contact Rota Yachting — Email, WhatsApp, phone and offices in Sinop, Istanbul and Amasra."
        }
      />
      <PageHeader
        eyebrow={t.contactPage.eyebrow}
        title={t.contactPage.title}
        intro={t.contactPage.intro}
        image="/assets/photos/photo-2024-06-05-18-08-35.jpg"
        testId="contact-header"
      />

      <section className="py-24 px-6 sm:px-10 lg:px-16" data-testid="contact-main">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5 space-y-10">
            <FadeUp>
              <div>
                <div className="overline mb-4">{t.contactPage.direct}</div>
                <ul className="space-y-5">
                  <li className="flex items-start gap-4">
                    <Mail size={18} className="text-brand mt-1 shrink-0" strokeWidth={1.5} />
                    <div>
                      <div className="text-[10px] tracking-[0.25em] uppercase text-navy/50 mb-1">Email</div>
                      <a
                        href={`mailto:${contactInfo.email}`}
                        className="text-navy hover:text-brand transition-colors"
                        data-testid="contact-email"
                      >
                        {contactInfo.email}
                      </a>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <Phone size={18} className="text-brand mt-1 shrink-0" strokeWidth={1.5} />
                    <div>
                      <div className="text-[10px] tracking-[0.25em] uppercase text-navy/50 mb-1">{t.footer.phone}</div>
                      <a
                        href={`tel:${contactInfo.phone.replace(/\s/g, "")}`}
                        className="block text-navy hover:text-brand transition-colors"
                        data-testid="contact-phone"
                      >
                        {contactInfo.phone}
                      </a>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <MessageCircle size={18} className="text-brand mt-1 shrink-0" strokeWidth={1.5} />
                    <div>
                      <div className="text-[10px] tracking-[0.25em] uppercase text-navy/50 mb-1">WhatsApp</div>
                      <a
                        href={`https://wa.me/${contactInfo.whatsapp}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-navy hover:text-brand transition-colors"
                        data-testid="contact-whatsapp"
                      >
                        {contactInfo.whatsappDisplay}
                      </a>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <Send size={18} className="text-brand mt-1 shrink-0" strokeWidth={1.5} />
                    <div>
                      <div className="text-[10px] tracking-[0.25em] uppercase text-navy/50 mb-1">Telegram</div>
                      <a
                        href={`https://t.me/+${contactInfo.whatsapp}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-navy hover:text-brand transition-colors"
                        data-testid="contact-telegram"
                      >
                        {contactInfo.whatsappDisplay}
                      </a>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <Instagram size={18} className="text-brand mt-1 shrink-0" strokeWidth={1.5} />
                    <div>
                      <div className="text-[10px] tracking-[0.25em] uppercase text-navy/50 mb-1">Instagram</div>
                      <a
                        href={contactInfo.instagramUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-navy hover:text-brand transition-colors"
                        data-testid="contact-instagram"
                      >
                        @{contactInfo.instagram}
                      </a>
                    </div>
                  </li>
                </ul>

                {/* Compact Sinop office map — under direct contacts */}
                <div className="mt-8" data-testid="contact-mini-map">
                  <div className="flex items-center justify-between mb-3">
                    <div className="overline">
                      {lang === "ru" ? "Найдите нас" : lang === "tr" ? "Bizi Bulun" : "Find us"}
                    </div>
                    <a
                      href="https://www.google.com/maps/place/Rota+Yachting+%26+Maritime+Agency+Turkey/@42.0242712,35.1473703,16.95z/data=!4m6!3m5!1s0x408f12c289f09df5:0xe10fb57ba2d0d55e!8m2!3d42.024786!4d35.149505!16s%2Fg%2F11b5vz681q"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[10px] tracking-[0.25em] uppercase font-semibold text-brand hover:text-brand-deep transition-colors"
                      data-testid="mini-map-directions"
                    >
                      {lang === "ru" ? "Маршрут →" : lang === "tr" ? "Yol Tarifi →" : "Directions →"}
                    </a>
                  </div>
                  <div className="relative w-full overflow-hidden border border-navy/10" style={{ height: 240 }}>
                    <iframe
                      src="https://www.google.com/maps?q=Rota+Yachting+%26+Maritime+Agency+Turkey,+Sinop&output=embed&z=16"
                      title="Rota Yachting Sinop Office"
                      width="100%"
                      height="100%"
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      className="block w-full h-full"
                      style={{ border: 0 }}
                    />
                  </div>
                </div>
              </div>
            </FadeUp>
          </div>

          <div className="lg:col-span-7">
            <FadeUp>
              <div className="glass p-8 sm:p-10">
                <div className="overline mb-3">{t.contactPage.formTitle}</div>
                <h2 className="font-display text-3xl sm:text-4xl text-navy leading-tight mb-8">
                  {lang === "ru"
                    ? "Расскажите о вашей яхте"
                    : lang === "tr"
                    ? "Yatınızı bize anlatın"
                    : "Tell us about your yacht"}
                </h2>
                <ContactForm />
              </div>
            </FadeUp>
          </div>
        </div>

        {/* Our offices — full-width 3-column row beneath the contact form */}
        <FadeUp>
          <div className="mt-16 sm:mt-20" data-testid="contact-offices-row">
            <div className="overline mb-6">{t.contactPage.offices}</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <OfficeCard office={offices.head} testId="office-head" />
              <OfficeCard office={offices.istanbul} testId="office-istanbul" />
              <OfficeCard office={offices.amasra} testId="office-amasra" />
            </div>
          </div>
        </FadeUp>
      </section>

      <section className="py-16 bg-cream border-t border-navy/10 px-6 sm:px-10 lg:px-16">
        <FadeUp>
          <div className="overline mb-6">{t.contactPage.registration}</div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-sm">
            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-navy/40 mb-2">Legal Name</div>
              <div className="text-navy">{companyDetails.legalName}</div>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-navy/40 mb-2">MERSIS No.</div>
              <div className="text-navy">{companyDetails.mersis}</div>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-navy/40 mb-2">Tax ID (Sinop)</div>
              <div className="text-navy">{companyDetails.taxId}</div>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-navy/40 mb-2">Ship Agency License</div>
            <div className="text-brand">No. {companyDetails.agencyLicense}</div>
            </div>
          </div>
        </FadeUp>
      </section>

      {/* Full-width Sinop office map removed per user request */}
    </>
  );
};

export default Contact;
